import { CloudOff, Lock, RefreshCw, SearchX, ShieldAlert, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsRestoring } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { EmptyState } from './EmptyState';
import { LoadingOverlay } from './LoadingOverlay';
import { isAccessDenial, isEdgeRejection, isNotFoundError } from '../../utils/errors';
import { useIsOfflineOptional } from '../../offline/OfflineStatusContext';

interface EntityLoadStateProps {
  /**
   * Entity key into the `common:entities.*` registry (e.g. `"deal"`, `"bills"`).
   * Resolved through i18n so terminology overrides apply ({{Deal}} etc.).
   * Use the SINGULAR key for detail pages and the PLURAL key for list pages,
   * matched to {@link variant}. Omit it for content that isn't a single named
   * entity (settings panels, reports, integrations) to get generic copy.
   */
  entityType?: string;
  /**
   * The fetched data. When present (not `undefined`/`null`) this component
   * renders nothing — the caller should render the real content instead. This
   * is what makes the offline-with-cache case "just work": cached data is
   * defined, so even though `error` is set from the failed background refetch,
   * no error state is shown and the page renders the cached entity.
   */
  data?: unknown;
  /** The query error, if any. Only consulted when there is no `data` to show. */
  error?: unknown;
  /** React Query `isLoading`. Shows a spinner when there is nothing cached yet. */
  isLoading?: boolean;
  /**
   * Retry handler — usually a React Query `refetch`. A retry button is shown
   * when provided. It is disabled while offline (nothing to retry until the
   * connection is back) and enabled for online load failures. The deny paths
   * (states 4, 5 and 6 below) show no button at all whether or not this is
   * passed: the same request is refused deterministically, so a retry cannot
   * succeed.
   */
  onRetry?: () => void;
  /**
   * Copy shape. `'detail'` → "This {{entity}} isn't available offline."
   * `'list'` → "{{entity}} aren't available offline." Defaults to `'detail'`.
   */
  variant?: 'detail' | 'list';
  className?: string;
}

/**
 * The single, system-wide offline-aware load state for every detail and list
 * page. It resolves one of four states from the standard React Query trio
 * (`data` / `error` / `isLoading`) plus the shared connectivity signal
 * ({@link useIsOffline} → `OfflineStatusContext`, Capacitor-backed on native):
 *
 * Connectivity is read via {@link useIsOfflineOptional} (a non-throwing variant
 * of `useIsOffline`) so this component degrades to "online" rather than crashing
 * a page that renders it outside an `OfflineStatusProvider` (tests, storybook).
 *
 * 1. **Data present** (online success, or offline-with-cache) → renders nothing;
 *    the caller renders the real content. Cached data is never hidden behind an
 *    error banner.
 * 2. **Loading, nothing cached** → spinner.
 * 3. **No data + offline** → "You're offline / This {{entity}} isn't available
 *    offline…" empty state (signal-loss icon, retry disabled).
 * 4. **No data + online 404** → "{{entity}} not found / doesn't exist or isn't
 *    shared with you" — the Contract-D 404-hide deny path. No retry (it can
 *    never succeed), no alarming "something went wrong" copy.
 * 5. **No data + a 403 carrying an error ENVELOPE** → "You don't have access" —
 *    the application evaluated the reader's standing and refused. No retry (the
 *    same request is refused for as long as that standing holds), and copy that
 *    names access rather than a system fault.
 * 6. **No data + a 403 carrying no error envelope** → "Blocked by a security
 *    filter" — the request never reached the application, so there is nothing
 *    to retry. Also no alarming "something went wrong" copy.
 * 7. **No data + other online failure (5xx, a legacy string-detail 403, …)** →
 *    "Failed to load {{entity}}" with an enabled retry. No connectivity hint.
 *
 * Render it in the page's "nothing to show" branch, wrapped in the page's own
 * `PageContainer`:
 *
 * ```tsx
 * if (!deal) {
 *   return (
 *     <PageContainer>
 *       <EntityLoadState entityType="deal" data={deal} error={error} isLoading={isLoading} onRetry={refetch} />
 *     </PageContainer>
 *   );
 * }
 * ```
 *
 * Read-query fallbacks only. Mutation/upload errors, toasts, and
 * auth/session-expired messages must NOT use this — they need explicit feedback
 * regardless of connectivity.
 */
export function EntityLoadState({
  entityType,
  data,
  error,
  isLoading = false,
  onRetry,
  variant = 'detail',
  className,
}: EntityLoadStateProps) {
  const { t } = useTranslation('common');
  const offline = useIsOfflineOptional();
  // React Query fetches nothing while the persisted cache is being restored, so
  // a query observed during that window reports `isLoading === false` with no
  // data and no error. That is still "loading" to the reader — never a failure.
  const isRestoring = useIsRestoring();

  // Data is available (fresh or cached) — let the caller render it.
  if (data !== undefined && data !== null) {
    return null;
  }

  // Nothing cached yet and a fetch is in flight (or about to be) — show a
  // spinner, not an error.
  if ((isLoading || isRestoring) && !error) {
    return <LoadingOverlay className={className} />;
  }

  const entity = entityType ? t(`entities.${entityType}`) : undefined;

  if (offline) {
    return (
      <EmptyState
        icon={CloudOff}
        title={t('offline.entityUnavailableTitle')}
        message={
          entity
            ? t(
                variant === 'list'
                  ? 'offline.entityUnavailableList'
                  : 'offline.entityUnavailableDetail',
                { entity }
              )
            : t('offline.genericUnavailable')
        }
        className={className}
      >
        {onRetry && (
          <Button variant="outline" size="sm" disabled className="mt-1">
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('app.retry')}
          </Button>
        )}
      </EmptyState>
    );
  }

  // Deny path: a 404 is the Contract-D "404-hide" — indistinguishable from
  // "doesn't exist". Never surface it as a system error with a dead retry;
  // show a calm not-found / no-access state instead. (A genuine 5xx falls
  // through to the retryable failure state below.)
  if (isNotFoundError(error)) {
    return (
      <EmptyState
        icon={SearchX}
        title={entity ? t('offline.entityNotFoundTitle', { entity }) : t('offline.genericNotFoundTitle')}
        message={
          entity ? t('offline.entityNotFoundMessage', { entity }) : t('offline.genericNotFoundMessage')
        }
        className={className}
      />
    );
  }

  // Second deny path: a 403 the APPLICATION answered with. `api_error(...)`
  // sends the `{ detail: { code, ... } }` envelope, so the backend ran,
  // evaluated this reader's standing and said no — PERMISSION_DENIED for a
  // record that isn't shared with them, ADMIN_ACCESS_REQUIRED from
  // `require_module_admin`, a portal feature guard. That answer is
  // deterministic for as long as the standing holds, so the failure state below
  // would offer a Retry that produces the identical 403 forever and would blame
  // a system fault for what is an access decision.
  //
  // The copy is deliberately the detail-page/load-failure sibling of
  // `RestrictedEmptyState` (the list-page zero-rows-because-of-access state)
  // and names the same remedy in the same words: ask an owner to share, or an
  // administrator to grant access. No button — there is no request-access route
  // in the product, and a reader who cannot load the record cannot open its
  // ShareDialog either, so the honest affordance is prose. If access IS granted
  // while the page is open, one reload costs less than a button that lies on
  // every other visit.
  //
  // The predicate reads the RAW envelope rather than `getErrorDetail`, so a
  // legacy STRING detail (`{"detail": "Not authenticated"}`) does NOT land here:
  // that shape is 401-shaped, our own routes cannot emit it, and a retry after a
  // token refresh can genuinely succeed. See `isAccessDenial` in
  // `shared/utils/errors.ts`.
  if (isAccessDenial(error)) {
    return (
      <EmptyState
        icon={Lock}
        title={t('offline.accessDeniedTitle')}
        message={t('offline.accessDeniedMessage')}
        className={className}
      />
    );
  }

  // Third deny path, for the same reason as the 404 above: a 403 carrying no
  // `{ detail }` envelope is not a load failure at all. Every application 403
  // has one (`api_error(...)`, and even FastAPI's own bearer rejection sends a
  // string `detail`), so reaching here means the request was refused BEFORE the
  // backend ran — in production by the WAF at the CloudFront edge
  // (`infrastructure/pulumi/components/waf.py`). It is reachable on a plain GET:
  // `GenericLFI_QUERYARGUMENTS` and `EC2MetaDataSSRF_QUERYARGUMENTS` are live in
  // BLOCK mode, so a search or filter value holding a path-like or
  // internal-host token trips it.
  //
  // The refusal is deterministic, so the failure state below would offer a Retry
  // that can never succeed and would blame a system fault for the reader's own
  // filter text. Name the cause and a remedy that can work instead — and, unlike
  // the toast-side `errors:EDGE_REQUEST_BLOCKED`, name the READ remedy (clear
  // the search or filters), not the write-side one (edit the submitted body).
  if (isEdgeRejection(error)) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title={t('offline.edgeBlockedTitle')}
        message={t('offline.edgeBlockedMessage')}
        className={className}
      />
    );
  }

  return (
    <EmptyState
      icon={TriangleAlert}
      title={entity ? t('offline.entityLoadFailedTitle', { entity }) : t('offline.genericLoadFailedTitle')}
      message={t('offline.entityLoadFailedMessage')}
      className={className}
    >
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-1 text-foreground">
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('app.retry')}
        </Button>
      )}
    </EmptyState>
  );
}
